import React from 'react';
import electronBridge from '../utils/electronBridge';

// Check if a path is an Electron file path (Windows or Unix)
const isElectronFilePath = (url) => {
  if (!url) return false;
  return electronBridge.isElectron && (url.includes(':\\') || (url.includes('://') && !url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('blob:')));
};

export const detectMediaType = (url) => {
  if (!url) return 'image';
  const lowerUrl = url.toLowerCase();

  if (lowerUrl.startsWith('data:')) {
    if (lowerUrl.startsWith('data:video/')) return 'video';
    if (lowerUrl.startsWith('data:audio/')) return 'audio';
    if (lowerUrl.startsWith('data:model/') || lowerUrl.startsWith('data:application/octet-stream')) return '3d';
    if (lowerUrl.startsWith('data:image/')) return 'image';
  }

  if (lowerUrl.includes('.mp4') || lowerUrl.includes('.webm') || lowerUrl.includes('.mov') ||
      lowerUrl.includes('.avi') || lowerUrl.includes('.mkv') || lowerUrl.includes('/video/') ||
      lowerUrl.includes('video_url') || lowerUrl.includes('type=video')) {
    return 'video';
  }

  if (lowerUrl.includes('.mp3') || lowerUrl.includes('.wav') || lowerUrl.includes('.ogg') ||
      lowerUrl.includes('.flac') || lowerUrl.includes('.m4a') || lowerUrl.includes('/audio/') ||
      lowerUrl.includes('audio_url') || lowerUrl.includes('type=audio')) {
    return 'audio';
  }

  if (lowerUrl.includes('.glb') || lowerUrl.includes('.gltf') || lowerUrl.includes('.obj') ||
      lowerUrl.includes('.fbx') || lowerUrl.includes('/3d/') || lowerUrl.includes('type=3d')) {
    return '3d';
  }

  // For Electron file paths, check file extensions for images
  if (isElectronFilePath(url)) {
    if (lowerUrl.includes('.png') || lowerUrl.includes('.jpg') || lowerUrl.includes('.jpeg') ||
        lowerUrl.includes('.gif') || lowerUrl.includes('.webp') || lowerUrl.includes('.bmp') ||
        lowerUrl.includes('.svg')) {
      return 'image';
    }
  }

  return 'image';
};

const mediaBackgroundColors = {
  video: 'bg-gradient-to-br from-purple-900/80 to-purple-800/60',
  audio: 'bg-gradient-to-br from-green-900/80 to-green-800/60',
  '3d': 'bg-gradient-to-br from-orange-900/80 to-orange-800/60',
};

const mediaTypeBadgeConfig = {
  image: {
    icon: (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    color: 'bg-blue-500/20 text-blue-400',
    label: 'Image',
  },
  video: {
    icon: (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    ),
    color: 'bg-purple-500/20 text-purple-400',
    label: 'Video',
  },
  audio: {
    icon: (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" />
      </svg>
    ),
    color: 'bg-green-500/20 text-green-400',
    label: 'Audio',
  },
  '3d': {
    icon: (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5" />
      </svg>
    ),
    color: 'bg-orange-500/20 text-orange-400',
    label: '3D',
  },
};

export function MediaTypeBadge({ type, className = '' }) {
  const config = mediaTypeBadgeConfig[type] || mediaTypeBadgeConfig.image;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${config.color} ${className}`}>
      {config.icon}
      {config.label}
    </span>
  );
}

export function MediaRenderer({ url, alt, className = '', showControls = false, mediaType: providedMediaType }) {
  const mediaType = providedMediaType || detectMediaType(url);

  if (mediaType === 'video') {
    return (
      <div className={`relative ${className}`} onClick={(e) => e.stopPropagation()}>
        <video
          src={url}
          className="w-full h-full object-cover"
          controls={showControls}
          muted
          loop
          playsInline
          onMouseEnter={(e) => !showControls && e.target.play()}
          onMouseLeave={(e) => !showControls && e.target.pause()}
        />
        {!showControls && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-10 h-10 rounded-full bg-purple-500/70 flex items-center justify-center">
              <svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (mediaType === 'audio') {
    return (
      <div
        className={`relative ${className} ${mediaBackgroundColors.audio} flex flex-col items-center justify-center p-2`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-10 h-10 rounded-full bg-green-500/30 flex items-center justify-center mb-2">
          <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" />
          </svg>
        </div>
        <span className="text-green-300 text-xs font-medium mb-2">Audio</span>
        <audio src={url} controls className="w-full max-w-full h-8" style={{ maxWidth: '100%' }} />
      </div>
    );
  }

  if (mediaType === '3d') {
    return (
      <div className={`relative ${className} ${mediaBackgroundColors['3d']} flex flex-col items-center justify-center`}>
        <div className="w-12 h-12 rounded-full bg-orange-500/30 flex items-center justify-center mb-2">
          <svg className="w-6 h-6 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5" />
          </svg>
        </div>
        <span className="text-orange-300 text-xs font-medium">3D Model</span>
      </div>
    );
  }

  return (
    <img
      src={url}
      alt={alt}
      className={`w-full h-full object-cover ${className}`}
      loading="lazy"
    />
  );
}
